import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SparklesIcon } from 'lucide-react';
import { BackLink, EmptyState, PageEyebrow, PageHeading, Panel, Pill, SearchInput } from '@bcecloud/portal-ui';
import { MarkdownArticle } from '@/components/MarkdownArticle';
import { findRelease, visibleRelease, visibleReleases, type Release } from '@/help/content';
import { useFeatureLookup } from '@/lib/app-features';
import { usePermissionLookup } from '@/lib/use-permission-lookup';
import { useGuide } from '@/guides/use-guide';

function releaseDate(date: string): string {
  const parsed = Date.parse(`${date}T00:00:00Z`);
  return Number.isNaN(parsed) ? date : new Date(parsed).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** What's new: every release, newest first, linking to the help pages and guides behind each change. */
export function WhatsNewPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const query = search.trim();
  const isFeatureOn = useFeatureLookup();
  const hasPermission = usePermissionLookup();
  const releases = visibleReleases(isFeatureOn, hasPermission, query);
  const runGuide = useGuide();
  return (
    <section className="animate-portal-rise">
      <PageHeading eyebrow="HELP" title="What's new" subtitle="What changed in the app and when, with a link to the help page for each." />
      <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
        <SearchInput label="Search releases" placeholder="A feature, a screen, a word from the notes" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full max-w-md" />
      </div>
      <div className="space-y-6">
        {visibleReleases(isFeatureOn, hasPermission).length === 0 ? (
          <Panel>
            <EmptyState icon={<SparklesIcon aria-hidden className="size-6" />} title="No releases yet" message="Release notes are written in docs/releases and appear here on the next deploy." />
          </Panel>
        ) : releases.length === 0 ? (
          <Panel>
            <EmptyState icon={<SparklesIcon aria-hidden className="size-6" />} title="Nothing matches" message="Try a different word, or the name of the feature." />
          </Panel>
        ) : (
          releases.map((release) => <ReleasePanel key={release.slug} release={release} onGuide={runGuide} />)
        )}
      </div>
    </section>
  );
}

/** One release on its own page, the target of the announcement popup's Read more. */
export function ReleasePage(): React.ReactElement {
  const { slug = '' } = useParams<{ slug: string }>();
  const isFeatureOn = useFeatureLookup();
  const hasPermission = usePermissionLookup();
  const found = findRelease(slug);
  const release = found ? visibleRelease(found, isFeatureOn, hasPermission) : undefined;
  const runGuide = useGuide();
  return (
    <section className="animate-portal-rise">
      <BackLink to="/whats-new">What's new</BackLink>
      <div className="mt-4">
        {release ? (
          <ReleasePanel release={release} onGuide={runGuide} standalone />
        ) : (
          <Panel>
            <EmptyState icon={<SparklesIcon aria-hidden className="size-6" />} title="No such release" message="It may have been renamed. Head back to What's new." />
          </Panel>
        )}
      </div>
    </section>
  );
}

function ReleasePanel({ release, onGuide, standalone = false }: { readonly release: Release; readonly onGuide: (id: string) => void; readonly standalone?: boolean }): React.ReactElement {
  return (
    <Panel className="p-6">
      <header className="mb-4">
        <PageEyebrow>{releaseDate(release.date)}</PageEyebrow>
        <div className="flex flex-wrap items-center gap-2">
          {standalone ? <h1 className="text-portal-page tracking-[-1.7px]">{release.title}</h1> : <h2 className="text-portal-section font-bold"><Link to={`/whats-new/${release.slug}`} className="hover:text-brand">{release.title}</Link></h2>}
          {release.announce ? <Pill tone="brand">Announced</Pill> : null}
        </div>
      </header>
      <MarkdownArticle markdown={release.markdown} onGuide={onGuide} />
    </Panel>
  );
}
